import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * SectionToggle — slim accordion wrapper used across CreateListingPage and its
 * extracted sub-components. Was previously defined inline in CreateListingPage.
 *
 * Renders a card-style header (icon + title + hint + chevron) and a body that
 * appears below when expanded. `defaultOpen` controls the initial state.
 *
 * Feb-2026 Iter 28 — Controlled-Mode: optionale `open` + `onOpenChange`-Props
 * für Akkordeon-Verhalten von außen (z.B. ExtraTechnicalSection Extras-Row).
 *
 * Feb-2026 Iter 28 (User-Wunsch 25 May): Beim Aufklappen wird die Section
 * automatisch oben im Viewport positioniert (96 px Sticky-Offset), damit
 * der gesamte Inhalt sichtbar ist ohne dass der User scrollen muss.
 */
export default function SectionToggle({
    title,
    titleExtra,
    icon: Icon,
    hint,
    testId,
    defaultOpen = false,
    open: openProp,
    onOpenChange,
    headerExtras,
    children,
}) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : internalOpen;
    const rootRef = useRef(null);
    const prevOpenRef = useRef(open);
    useEffect(() => {
        if (open && !prevOpenRef.current && rootRef.current) {
            const t = setTimeout(() => {
                const el = rootRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const STICKY_OFFSET = 96;
                const targetTop = window.scrollY + rect.top - STICKY_OFFSET;
                window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
            }, 60);
            return () => clearTimeout(t);
        }
        prevOpenRef.current = open;
        return undefined;
    }, [open]);
    const toggle = () => {
        const next = !open;
        if (!isControlled) setInternalOpen(next);
        if (onOpenChange) onOpenChange(next);
    };
    return (
        <div ref={rootRef} data-testid={testId}>
            <div className="w-full flex items-center gap-3 px-6 py-4 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 transition-colors mb-2">
                <button
                    type="button"
                    onClick={toggle}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={testId ? `${testId}-btn` : undefined}
                    aria-expanded={open}
                >
                    {Icon && (
                        <div className="w-9 h-9 bg-[#FFCC00] flex items-center justify-center rounded-sm shrink-0">
                            <Icon className="w-4 h-4 text-[#111827]" strokeWidth={2.5} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-bold leading-tight truncate">
                            {title}
                            {/* Aug-2026 (User-Wunsch): Fahrzeugtitel rechts neben der Überschrift */}
                            {titleExtra && (
                                <span className="ml-2.5 text-sm font-bold text-gray-400 align-middle" data-testid={testId ? `${testId}-title-extra` : undefined}>
                                    {titleExtra}
                                </span>
                            )}
                        </h2>
                        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
                    </div>
                </button>
                {headerExtras && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        {headerExtras}
                    </div>
                )}
                <button
                    type="button"
                    onClick={toggle}
                    className="shrink-0 p-1 -mr-1 text-gray-400 hover:text-gray-700"
                    aria-label={open ? "Einklappen" : "Ausklappen"}
                    aria-expanded={open}
                >
                    <ChevronDown
                        className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`}
                        aria-hidden="true"
                    />
                </button>
            </div>
            {open && <div data-testid={testId ? `${testId}-body` : undefined}>{children}</div>}
        </div>
    );
}
